import { TeacherMe } from '@gmm/sdk/shared';
import { ApiSdkSwrKey, swrKeyFor } from '~/lib/swr';

type Teacher = Pick<TeacherMe, 'id' | 'exerciseFolderRootId'>;

interface SkillDiscoveryOptions {
  // chosen from vertical selections, ex. '2nd Grade', '3rd Grade', ..., 'colleagues'
  selectedGrade: string | null;
  // chosen from top tabs, ex. 'work', 'school', 'district', 'new'
  selectedCollection: { id: string } | null;
  teacher: Teacher | undefined;
}

// Keep ts happy when inbound selectedCollection.id is 'school' or 'district'
// and we turn around and use that value as the outbound type (which is constrained in the sdk file
// for skillDiscovery.show)
const colleagueTypeMap: Record<'school' | 'district', 'SCHOOL' | 'DISTRICT'> = {
  school: 'SCHOOL',
  district: 'DISTRICT',
} as const;

// validate tag IDs
const isUuid = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );

/*
  Note to self: in this context, "selectedGraded" directly correlates to the vertical
  bar in the Skill Discovery UI: 2nd grade, 3rd grade, etc.  The "colleagues" option for selectedGrade is a special case,
  the bottom of the vertical bar. It arrives here as a 'grade', but really it is a signal
  that the "selectedCollection.id" will be in this set: "work", "school", or "district".

  For all the other 'normal' selectedGrades, the "selectedCollection.id" will be a uuid for a
  Tag in the db UNLESS the selectedCollection.id is "new".  In that case, we are loading
  new skills for a particular grade (selectedGrade).
*/
export const getValueKey = (
  options: SkillDiscoveryOptions,
): ApiSdkSwrKey<'skillDiscovery.show'> => {
  const { selectedCollection, selectedGrade, teacher } = options;

  if (!selectedCollection || !selectedGrade || !teacher) {
    return swrKeyFor(() => null);
  }

  const isColleagues = selectedGrade === 'colleagues';

  // 'colleagues' selected as grade, so top tabs are 'work', 'school', or 'district'
  if (isColleagues) {
    switch (selectedCollection.id) {
      case 'work':
        // type is FOLDER, id is exerciseFolderRootId
        if (!teacher.exerciseFolderRootId) {
          console.error(
            'Missing exerciseFolderRootId for teacher in colleagues:work mode',
          );
          return swrKeyFor(() => null);
        }
        return swrKeyFor([
          'skillDiscovery.show',
          String(teacher.exerciseFolderRootId),
          'FOLDER',
        ]);

      case 'school':
      case 'district':
        // type is SCHOOL or DISTRICT, id is teacher.id
        if (!teacher || !teacher.id) {
          console.error(
            'Missing teacher or teacher.id for teacher in colleagues:school/district mode',
          );
          return swrKeyFor(() => null);
        }
        return swrKeyFor([
          'skillDiscovery.show',
          String(teacher.id),
          colleagueTypeMap[selectedCollection.id],
        ]);

      default:
        console.error(
          'Invalid Skill Discovery collection ID in "colleagues" mode:',
          selectedCollection.id,
        );
        return swrKeyFor(() => null);
    }
  } else if (selectedCollection.id === 'new') {
    // type is NEW, id is selectedGrade
    return swrKeyFor(['skillDiscovery.show', selectedGrade, 'NEW']);
  } else if (isUuid(selectedCollection.id)) {
    // type is TAG, id is tag's uuid
    return swrKeyFor(['skillDiscovery.show', selectedCollection.id, 'TAG']);
  }

  // Fallback for invalid non-colleague collection
  console.error(
    'Invalid Skill Discovery collection ID in "normal" mode:',
    selectedCollection.id,
  );
  return swrKeyFor(() => null);
};
